import React, { useCallback } from "react";
import { Formik } from "formik";

import ContainerFormButtons from "./ContainerFormButtons";
import * as Yup from "yup";
import FormikNumberInput from "../../../../formik-form-components/FormikNumberInput";
import { MainContainer, Title } from "./PhysicalInventoryStyles";
import { FormContainer } from "../../../../../constants/Styles";
import { useCustomAlert } from "../../../../../utils/hooks/alerts";
import ContainerSummary from "../../../../internal/parts/InventoryUpdates/ContainerSummary";
import useTriggerSounds from "../../../../../utils/hooks/sounds";
import useUpdateContainerQuantity from "../../../../../utils/hooks/inventory/updateContainerQuantity";
import useContainerHistorySummary from "../../../../../utils/hooks/inventory/containerHistory";
import { Container } from "../../../../../reducers/componentSearch";
import useContainer from "../../../../../utils/hooks/inventory/getContainer";
import AllocationsSummary from "../../../../internal/parts/InventoryUpdates/AllocationsSummary";

interface UpdateContainerQuantityFormValues {
  quantity: number | null;
}

export const MAX_CONTAINER_QUANTITY = 99999;
export const updateContainerQuantitySchema = Yup.object().shape({
  quantity: Yup.number()
    .min(0)
    .max(
      MAX_CONTAINER_QUANTITY,
      `Quantity cannot exceed ${MAX_CONTAINER_QUANTITY.toLocaleString()}`,
    )
    .required("Quantity is required")
    .nullable(),
});

const UpdateContainerQuantity = ({
  container,
  onCancel,
  onFinishedEditing,
}: {
  container: Container;
  onCancel: () => void;
  onFinishedEditing: () => void;
}) => {
  const { triggerGoodSound, triggerBadSound } = useTriggerSounds();
  const customAlert = useCustomAlert();
  const { refreshContainer } = useContainer(
    container.flashpcb_container_barcode,
  );
  const { fetchHistory } = useContainerHistorySummary();

  const updateContainerQuantitySchema = (minQuantity: number) => {
    return Yup.object().shape({
      quantity: Yup.number()
        .min(
          minQuantity,
          container.active_allocations_total > 0
            ? `Quantity must be at least ${minQuantity.toLocaleString()} to cover active allocations`
            : "Quantity cannot be negative.",
        )
        .max(
          MAX_CONTAINER_QUANTITY,
          `Quantity cannot exceed ${MAX_CONTAINER_QUANTITY.toLocaleString()}`,
        )
        .required("Quantity is required")
        .nullable(),
    });
  };

  const onSuccess = useCallback(() => {
    triggerGoodSound();
    fetchHistory();
    onFinishedEditing();
  }, [fetchHistory, onFinishedEditing, triggerGoodSound]);

  const onErrorContainerNotFound = useCallback(
    (detail: string) => {
      triggerBadSound();
      customAlert(detail, onCancel);
    },
    [customAlert, onCancel, triggerBadSound],
  );

  const onErrorUpdatingQuantity = useCallback(
    (detail: string) => {
      triggerBadSound();
      customAlert(detail);
    },
    [customAlert, triggerBadSound],
  );

  const { updateContainerQuantity, loading } = useUpdateContainerQuantity(
    onSuccess,
    onErrorUpdatingQuantity,
  );

  const onSubmit = (values: UpdateContainerQuantityFormValues) => {
    if (values.quantity !== null) {
      updateContainerQuantity(
        container.flashpcb_container_barcode,
        values.quantity,
      );
    }
  };

  return (
    <>
      <MainContainer>
        <Title>Update inventory level</Title>
        <ContainerSummary
          containerBarcode={container.flashpcb_container_barcode}
          onError={onErrorContainerNotFound}
        >
          {container.active_allocations_total > 0 && (
            <AllocationsSummary
              container={container}
              onUpdate={refreshContainer}
            />
          )}
          <Formik
            initialValues={{
              quantity: null,
            }}
            onSubmit={onSubmit}
            validationSchema={updateContainerQuantitySchema(
              container.active_allocations_total,
            )}
          >
            {({ handleSubmit, isValid, dirty }) => {
              return (
                <FormContainer onSubmit={handleSubmit}>
                  <FormikNumberInput
                    name="quantity"
                    label={"Quantity"}
                    disabled={false}
                    autoFocus={true}
                  />
                  <ContainerFormButtons
                    onCancel={onCancel}
                    loading={loading}
                    isDisabled={!(isValid && dirty)}
                    onSubmit={handleSubmit}
                  />
                </FormContainer>
              );
            }}
          </Formik>
        </ContainerSummary>
      </MainContainer>
    </>
  );
};

export default UpdateContainerQuantity;
